import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Snackbar from "@mui/material/Snackbar";
import Slide from "@mui/material/Slide";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useState, useEffect, useLayoutEffect } from "react";
import { Link as RouterLink, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import Header from "../core/Header";
import Footer from "../core/Footer";
import PageHero from "../components/PageHero";
import { useTranslation } from "../utils/i18n";
import {
  smdProducts,
  radioLogicProducts,
  antennaProducts,
} from "../data/products";
import { autecProductGroups } from "../data/autecData";

const PRODUCT_IMAGE = "/images/produkt.png";
const MAX_VISIBLE_PRODUCTS = 4;

export default function Products() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { t, resolve } = useTranslation();

  const [snackbarOpen, setSnackbarOpen] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = sessionStorage.getItem("products-expanded");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useLayoutEffect(() => {
    const saved = sessionStorage.getItem("products-scroll");
    const shouldRestore = navigationType === "POP" || location.state?.restoreScroll;
    if (shouldRestore && saved !== null) {
      window.scrollTo(0, parseInt(saved, 10));
    } else {
      window.scrollTo(0, 0);
    }
  }, [navigationType, location.state]);

  useEffect(() => {
    const handleScroll = () => {
      sessionStorage.setItem("products-scroll", String(window.scrollY));
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    sessionStorage.setItem("products-expanded", JSON.stringify(expandedGroups));
  }, [expandedGroups]);
  const isAutecView = location.pathname.startsWith("/autecproducts");
  const detailsBasePath = isAutecView ? "/autecproducts" : "/products";

  const productGroups = [
    {
      id: "smd",
      title: "SMD",
      description: t("productsPage.groups.smd.description"),
      cta: t("productsPage.groups.smd.cta"),
      accent: "#FF6A00",
      items: smdProducts,
    },
    {
      id: "radiologic",
      title: "RADIOLOGIC",
      description: t("productsPage.groups.radiologic.description"),
      cta: t("productsPage.groups.radiologic.cta"),
      accent: "#FF6A00",
      items: radioLogicProducts,
    },
    {
      id: "antenner",
      title: t("productsPage.groups.antenner.title"),
      description: t("productsPage.groups.antenner.description"),
      cta: t("productsPage.groups.antenner.cta"),
      accent: "#FF6A00",
      items: antennaProducts,
    },
  ];

  const visibleGroups = isAutecView ? autecProductGroups : productGroups;

  const toggleGroupExpansion = (groupTitle) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupTitle]: !prev[groupTitle],
    }));
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#F6F7F9",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      <PageHero
        overline={
          isAutecView
            ? "AUTEC SAFETY"
            : t("productsPage.breadcrumbProducts").toUpperCase()
        }
        title={
          isAutecView
            ? t("productsPage.titleAutec")
            : t("productsPage.titleProducts")
        }
        subtitle={
          isAutecView
            ? t("productsPage.subtitleAutec")
            : t("productsPage.subtitleProducts")
        }
        bgImage={isAutecView ? "/images/partners/AUTEC_bg.png" : undefined}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: 1440,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 5 },
          pt: { xs: 3, md: 4 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
            <Link
              component={RouterLink}
              to="/"
              underline="none"
              sx={{ color: "#4b4b4b" }}
            >
              {t("productsPage.breadcrumbHome")}
            </Link>
            <Typography aria-current="page" sx={{ color: "#FF6A00" }}>
              {isAutecView
                ? t("productsPage.breadcrumbAutec")
                : t("productsPage.breadcrumbProducts")}
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 4, md: 5 },
          }}
        >
          {visibleGroups.map((group, groupIndex) => (
            <Box key={group.id ?? group.title}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "280px minmax(0, 1fr)",
                  },
                  gap: { xs: 3, md: 4 },
                  alignItems: "start",
                  py: { xs: 2, md: 3 },
                }}
              >
                <Box sx={{ maxWidth: 300, pr: { md: 1 } }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "#0B0F13",
                      letterSpacing: "-0.02em",
                      mb: 1,
                      mt: 5,
                    }}
                  >
                    {resolve(group.title)}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#475467",
                      lineHeight: 1.7,
                      maxWidth: 270,
                    }}
                  >
                    {resolve(group.description)}
                  </Typography>
                </Box>

                <Box id={`group-${groupIndex}`}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(4, minmax(0, 1fr))",
                      },
                      gap: 2.25,
                    }}
                  >
                    {(expandedGroups[group.id ?? group.title]
                      ? group.items
                      : group.items.slice(0, MAX_VISIBLE_PRODUCTS)
                    ).map((product, index) => (
                      <Card
                        key={`${group.id ?? group.title}-${product.slug}-${index}`}
                        elevation={0}
                        sx={{
                          height: "100%",
                          borderRadius: 3,
                          border: "1px solid #E3E6EB",
                          bgcolor: "#fff",
                          overflow: "hidden",
                          transition:
                            "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                          "&:hover": {
                            transform: "translateY(-6px)",
                            boxShadow: "0 18px 32px rgba(24, 18, 15, 0.1)",
                            borderColor: "#CBD5E1",
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() =>
                            navigate(`${detailsBasePath}/${product.slug}`)
                          }
                          sx={{ height: "100%", alignItems: "stretch" }}
                        >
                          <Box
                            sx={{
                              pt: 2.5,
                              px: 2.5,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: 170,
                            }}
                          >
                            <Box
                              component="img"
                              src={product.image || PRODUCT_IMAGE}
                              alt={resolve(product.title ?? product.name)}
                              sx={{
                                maxWidth: "100%",
                                maxHeight: 150,
                                objectFit: "contain",
                              }}
                            />
                          </Box>

                          <Divider sx={{ mx: 2.5, opacity: 0.55 }} />

                          <CardContent sx={{ px: 2.5, pb: 2.5, pt: 1.75 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 800, color: "#0B0F13" }}
                            >
                              {resolve(product.title ?? product.name)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.75,
                                color: "rgba(30, 26, 24, 0.7)",
                                lineHeight: 1.55,
                              }}
                            >
                              {resolve(product.description ?? product.shortDescription)}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    ))}
                  </Box>

                  {group.items.length > MAX_VISIBLE_PRODUCTS && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mt: 2.25,
                      }}
                    >
                      <Divider sx={{ flex: 1, opacity: 0.55 }} />
                      <Button
                        onClick={() => toggleGroupExpansion(group.id ?? group.title)}
                        startIcon={
                          expandedGroups[group.id ?? group.title] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )
                        }
                        sx={{
                          textTransform: "none",
                          color: "#0B0F13",
                          fontWeight: 700,
                          px: 1,
                          minWidth: "auto",
                          "&:hover": {
                            bgcolor: "transparent",
                            color: "#000",
                          },
                        }}
                      >
                        {expandedGroups[group.id ?? group.title]
                          ? t("productsPage.showFewer")
                          : resolve(group.cta)}
                      </Button>
                      <Divider sx={{ flex: 1, opacity: 0.55 }} />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {!isAutecView && (
          <Box
            sx={{
              mt: { xs: 5, md: 7 },
              pt: { xs: 5, md: 7 },
              borderTop: "1px solid #E3E6EB",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{ color: "#FF6A00", letterSpacing: 2, fontWeight: 700 }}
              >
                {t("productsPage.autecPromo.overline")}
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#0B0F13", mt: 0.5 }}
              >
                {t("productsPage.autecPromo.title")}
              </Typography>
              <Typography sx={{ color: "#475467", mt: 0.5, maxWidth: 480 }}>
                {t("productsPage.autecPromo.subtitle")}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate("/autecproducts")}
              sx={{
                bgcolor: "#FF6A00",
                color: "#fff",
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                px: 4,
                py: 1.25,
                whiteSpace: "nowrap",
                flexShrink: 0,
                boxShadow: "none",
                "&:hover": { bgcolor: "#E05500", boxShadow: "none" },
              }}
            >
              {t("productsPage.autecPromo.btn")}
            </Button>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        TransitionComponent={(props) => <Slide {...props} direction="right" />}
        sx={{ mb: { xs: 1.5, md: 2.5 }, ml: { xs: 1.5, md: 3 } }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Box
          role="region"
          aria-label="Kontakt-snackbar"
          sx={{
            width: { xs: "80vw", md: "22vw" },
            minWidth: 240,
            bgcolor: "rgba(13, 17, 23, 0.82)",
            backdropFilter: "blur(8px)",
            color: "#fff",
            borderRadius: 2,
            px: { xs: 1.5, md: 2 },
            py: { xs: 1, md: 1.25 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
              {t("productsPage.snackbar.title")}
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.8rem" }}
            >
              {t("productsPage.snackbar.subtitle")}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              onClick={() => navigate("/contact")}
              sx={{
                bgcolor: "#FF6A00",
                borderRadius: 999,
                textTransform: "none",
                px: 1.75,
                py: 0.6,
                fontSize: "0.82rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "#E05500" },
              }}
            >
              {t("productsPage.snackbar.btn")}
            </Button>

            <IconButton
              aria-label="Stäng snackbar"
              onClick={() => setSnackbarOpen(false)}
              sx={{ color: "#fff", ml: -0.5 }}
              size="large"
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </Box>
      </Snackbar>
      <Footer />
    </Box>
  );
}
